import styled from "styled-components";
import SiteHeader from "../landing/components/Header/SiteHeader";
import Footer from "../landing/components/Footer/Footer";
import useFocusPageStart from "../../hooks/useFocusPageStart";
import Breadcrumb from "../about/components/Breadcrumb/Breadcrumb";

const screenReaders = [
  ["Screen Access For All (SAFA)", "https://sourceforge.net/projects/safa/lists/safa-developer", "Free"],
  ["Non Visual Desktop Access (NVDA)", "https://www.nvaccess.org/", "Free"],
  ["System Access To Go", "http://www.satogo.com", "Free"],
  ["Thunder", "http://www.webbie.org.uk/thunder", "Free"],
  ["WebAnywhere", "http://webinsight.cs.washington.edu/", "Free"],
  ["Hal", "http://www.yourdolphin.co.uk/productdetail.asp?id=5", "Commercial"],
  ["JAWS", "https://www.freedomscientific.com/Downloads/JAWS", "Commercial"],
  ["Supernova", "http://www.yourdolphin.co.uk/productdetail.asp?id=1", "Commercial"],
  ["Window-Eyes", "http://www.gwmicro.com/Window-Eyes/", "Commercial"],
];

const Page = styled.div`
  min-height: 100vh;
  background: #f7f7f7;
`;

const BreadcrumbWrapper = styled.nav`
  padding: 8px 80px 16px;
  @media (max-width: 768px) { padding: 8px 16px 12px; }
`;

const TitleWrapper = styled.div`
  padding: 28px 80px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h1 {
    margin: 0;
    color: #1b3a6b;
    font-size: 23px;
    line-height: 1.2;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  h1::first-letter { color: #ff9933; }
  @media (max-width: 768px) {
    padding: 6px 16px 14px;
    h1 { font-size: 20px; }
  }
`;

const Content = styled.div`
  width: calc(100% - 40px);
  max-width: 1192px;
  margin: 8px auto 0;
  padding: 54px 64px 48px;
  border: 1px solid #e8e8e8;
  border-radius: 20px;
  background: #fafafa;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #30465d;
  font-size: 16px;
  line-height: 1.7;
  outline: none;

  h2 { margin: 12px 0 4px; color: #000; font-size: 26px; font-weight: 700; line-height: 1.4; }
  h3 { margin: 26px 0 8px; color: #111; font-size: 18px; font-weight: 700; }
  p { margin: 0 0 26px; }
  a { color: #2318ff; overflow-wrap: anywhere; }
  a:hover { text-decoration: underline; }
  a:focus-visible, button:focus-visible { outline: 2px solid #174586; outline-offset: 3px; }
  .table-scroll { overflow-x: auto; margin: 44px 16px 28px; }
  table { width: 100%; border-collapse: collapse; color: #000; text-align: left; line-height: 1.5; }
  caption { color: #30465d; padding: 12px 0 22px; text-align: center; }
  th, td { border: 1px solid #cfd3db; padding: 9px 15px; vertical-align: top; }
  th { background: #dce9fb; font-weight: 700; }
  .readers { min-width: 720px; }
  .readers th:nth-child(1) { width: 31%; }
  .readers th:nth-child(3) { width: 18%; }

  @media (max-width: 760px) {
    width: calc(100% - 24px);
    padding: 28px 20px;
    h2 { font-size: 22px; }
    .table-scroll { margin: 28px 0; }
  }
`;

const ExternalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
);

export default function HelpPage() {
  useFocusPageStart("/help");

  return (
    <Page>
      <SiteHeader className="shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
        <BreadcrumbWrapper aria-label="Breadcrumb">
          <Breadcrumb items={[{ label: "Home", path: "/" }, { label: "Help", path: "/help" }]} />
        </BreadcrumbWrapper>
      </SiteHeader>
      <main id="main-content" data-readable-content tabIndex={-1} className="outline-none">
        <TitleWrapper>
          <h1>Help</h1>
        </TitleWrapper>
      <Content>
        <p>Are you finding it difficult to access/navigate through the content/pages of this Portal? This section attempts to help you have a pleasant experience while browsing this Portal.</p>

        <h2>Viewing Information in Various File Format</h2>
        <p>The information provided by this Web site is available in various formats, such as Portable Document Format (PDF). To view the information properly, your browser needs to have the required plug-ins or software. For example, PDF reader software can be used to view PDF documents. If your system does not have this software, you can download a reader from the Internet for free. The table lists tools to view the information in various file formats.</p>

        <div className="table-scroll" role="region" aria-label="File format information" tabIndex={0}>
          <table>
            <caption>Information in File Formats</caption>
            <thead><tr><th scope="col">Document Type</th><th scope="col">Plug-in for Download</th></tr></thead>
            <tbody><tr>
              <td>Portable Document Format (PDF) files</td>
              <td><ExternalLink href="https://get.adobe.com/reader/">Adobe Acrobat Reader</ExternalLink>{", "}<ExternalLink href="https://www.adobe.com/acrobat/online/pdf-to-word.html">Convert a PDF file online</ExternalLink></td>
            </tr></tbody>
          </table>
        </div>

        <h3>Screen Reader Access</h3>
        <p>Visitors with visual impairments can access the Portal using assistive technologies, such as screen readers. Accessibility guidance is available in the <ExternalLink href="https://guidelines.india.gov.in/">Guidelines for Indian Government Websites</ExternalLink>.</p>
        <p>Screen readers include JAWS, NVDA, SAFA, Supernova and Window-Eyes.</p>
        <p>Following table lists the information about different screen readers:</p>

        <div className="table-scroll" role="region" aria-label="Screen reader information" tabIndex={0}>
          <table className="readers">
            <caption>Information related to the various screen readers</caption>
            <thead><tr><th scope="col">Document Type</th><th scope="col">Plug-in for Download</th><th scope="col">Free / Commercial</th></tr></thead>
            <tbody>{screenReaders.map(([name, url, type]) => (
              <tr key={name}><td>{name}</td><td><ExternalLink href={url}>{url}</ExternalLink></td><td>{type}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </Content>
      </main>
      <Footer />
    </Page>
  );
}
